import { UnconvertedCurrencyBalance } from "@/components/l10n/Currency.tsx";
import type { BillingAccountCardWithData } from "@/modules/billing/billingCardsQuery.ts";
import type { EBillingAccount } from "@/modules/billing/billingLogic.tsx";
import { getCalculatedFees, getCountryCurrencyFees } from "@/modules/billing/payment/fees.ts";
import type { MethodFees } from "@/modules/billing/paymentMethods.ts";
import { currenciesAtom } from "@/modules/currency/currencyInfo.ts";
import type { SiteAmount } from "@/modules/currency/currencyTypes.ts";
import { ExchangeRateExample } from "@/modules/currency/ExchangeRateExample.tsx";
import { useCurrentCurrency } from "@/modules/currency/useCurrentCurrency.ts";
import { softInvariant } from "@/modules/error/errorStream.ts";
import { Trans } from "@lingui/react/macro";
import { useAtomValue } from "jotai/react";

export function ConversionMessage({
    account,
    amount,
    fees,
    card,
}: {
    account: EBillingAccount;
    amount: SiteAmount;
    fees: MethodFees;
    card: BillingAccountCardWithData | undefined;
}) {
    //#region Hooks
    const currency = useCurrentCurrency();
    const currencies = useAtomValue(currenciesAtom);
    //#endregion

    const charge_currency_conversion = fees?.charge_currency_conversion;
    if (!charge_currency_conversion) {
        return;
    }

    const country =
        card?.parsedAdditionalData.card_type === "PayPal"
            ? undefined
            : card?.parsedAdditionalData.processor_data?.country;

    const conversion = getCountryCurrencyFees(account, charge_currency_conversion, country);
    if (!conversion) {
        return;
    }

    const { total } = getCalculatedFees(fees, amount, account.account.vat_percentage);
    let content;
    if (currency.code === conversion.to_currency) {
        content = <Trans>With the selected payment method, you will be charged in {currency.title}.</Trans>;
    } else {
        const toCurrency = currencies?.find((f) => f.code === conversion.to_currency);
        // XXX: This should always be found?
        softInvariant(!!toCurrency, "Conversion to currency not found");
        if (toCurrency) {
            const convertedAmount = toCurrency.fromSite(total);
            content = (
                <>
                    <Trans>
                        Your actual charge will be{" "}
                        <UnconvertedCurrencyBalance currency={conversion.to_currency} value={convertedAmount} />, with
                        exchange rate of <ExchangeRateExample currency={toCurrency} />.
                    </Trans>
                    <br />
                    <Trans>
                        The final exchange rate and charge amount will be determined at the moment of actual payment.
                    </Trans>
                </>
            );
        }
    }

    return content && <div className="font-size-small color-muted">{content}</div>;
}
